import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import _ from "lodash";
import { API } from "aws-amplify";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { throttle } from "lodash";
import ContentLoader from "react-content-loader";
import moment from "moment";

import { Page, Icon, SetupGuide } from "components";
import { EmptyState, SearchDropdown, DateRange, Button } from "components";
import { ReactComponent as Empty } from "svgs/empty.svg";
import ShopContext from "context/ShopContext";
import classNames from "lib/classNames";
import { chartOptions, stateCards } from "lib/globals";
import { getShopifyCartItems } from "../components/cart/Utils";
import styles from "styles/dashboard.module.scss";
import { formatMoney } from "components/cart/Utils";

window.mo = moment;
const defaultStateCards = {
  CartsDisplayed: null,
  SalesGenerated: null,
  SalesAmount: null,
  AverageOrderSize: null,
  CartConversionRate: null,
  UpsellsGenerated: null,
  UpsellsTotalAmount: null,
  TotalROI: null,
};
const DashboardScreen = (props) => {
  const [activeChart, setActiveChart] = useState("CartsDisplayed");
  const [loading, setLoading] = useState(false);
  const [campaignsFilter, setCampaignsFilter] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  const [chartSeries, setChartSeries] = useState([
    {
      name: "test",
      data: [1, 2, 3],
    },
  ]);
  const [count, setCount] = useState(defaultStateCards);
  const { state: shop } = useContext(ShopContext);
  const formatter = (val, opts) => {
    switch (activeChart) {
      // case "views":
      //   return `${val} views`;
      case "revenue":
        return `${val} $`;
      case "CartConversionRate":
        return `${val} %`;
      default:
        return val;
    }
  };
  const getChart = async (newActiveChart = "CartsDisplayed") => {
    setActiveChart(newActiveChart);
    setLoading(true);
    
    const dateFilter = dateRangeFilter
      ? {
          start: moment(dateRangeFilter[0]).utcOffset(10).startOf('day').valueOf(),
          end: moment(dateRangeFilter[1]).utcOffset(10).endOf('day').valueOf(),
        }
      : {};
      
    try {
      const data = await API.get("API_GATEWAY_NEW", "/analytics/chart", {
        queryStringParameters: Object.assign(
          {
            type: newActiveChart,
            campaigns: campaignsFilter,
          },
          dateFilter
        ),
      });
      
      const chartData = data[newActiveChart];
      let processedData = [];
      
      if (Array.isArray(chartData) && chartData.length > 0) {
        processedData = chartData
          .map((point) => {
            const { x = 0, y = 0 } = point || {};
            return x ? { x, y: y || 0 } : undefined;
          })
          .filter((p) => !!p);
      }
      
      setChartSeries([
        {
          name: newActiveChart,
          data: processedData,
        },
      ]);
    } catch (error) {
      console.error('Chart data error:', error);
      setChartSeries([
        {
          name: newActiveChart,
          data: [],
        },
      ]);
    }
    
    setLoading(false);
  };
  const getCount = async () => {
    setCount(defaultStateCards);
    const dateFilter = dateRangeFilter
      ? {
          start: moment(dateRangeFilter[0]).utcOffset(10).startOf("day").valueOf(),
          end: moment(dateRangeFilter[1]).utcOffset(10).endOf("day").valueOf(),
        }
      : {};
    const count = await API.get("API_GATEWAY_NEW", "/analytics/count", {
      queryStringParameters: Object.assign(
        {
          campaigns: campaignsFilter,
        },
        dateFilter
      ),
    });
    setCount(count);
  };
  const searchCampaigns = (keyword) =>
    API.get("API_GATEWAY_NEW", "/campaigns/search", {
      queryStringParameters: { keyword },
    });
  const searchCampaignsThrottle = throttle(searchCampaigns, 700);

  useEffect(() => {
    getCount();
    getChart();
    return () => {
      console.log("dashboard screen cleanup");
    };
  }, [campaignsFilter, dateRangeFilter]);
  return (
    <Page
      header={{
        title: "Dashboard",
        actions: [],
      }}
      loading={{
        text: "Loading",
        status: false,
      }}
      background="none"
      padding={0}
    >
      <div
        className={classNames(
          "grid grid-flow-row gap-8 grid-cols-1 lg:grid-cols-3"
        )}
      >
        <div
          className={classNames(
            "col-span-1 lg:col-span-3 flex items-center flex-wrap space-y-3 sm:space-y-0 sm:space-x-3"
          )}
        >
          <b className={classNames("flex-1 text-gray-600 text-lg")}>Stats</b>
          {/* <SearchDropdown
						className={classNames('flex-auto w-full sm:flex-1')}
						placeholder="Campaigns Filter"
						promise={(keyword) => searchCampaignsThrottle(keyword)}
						onChange={(selected) => {
							setCampaignsFilter(selected ? selected.map(({ value }) => value) : []);
						}}
					/> */}
          <DateRange
            onChange={(range) => {
              setDateRangeFilter(range);
            }}
            maxDate={new Date()}
            value={dateRangeFilter}
            className={classNames("flex-auto w-full sm:flex-1")}
            button={{
              text: "Date Filter",
              icon: "calendar-alt",
            }}
          />
        </div>
        <div
          className={classNames(
            "col-span-1 lg:col-span-3 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          )}
        >
          {stateCards.map((stateCard, i) => (
            <StateCard
              {...stateCard}
              value={count[stateCard.title]}
              key={i}
              position={i + 1}
              onClick={() => {
                if (stateCard.clickable === false) return;
                getChart(stateCard.title);
              }}
              active={activeChart == stateCard.title.toString() ? 1 : 0}
            />
          ))}
        </div>
        <div
          className={classNames(
            "col-span-2 lg:col-span-3 bg-white p-3 relative rounded p-6 shadow"
          )}
        >
          <Chart
            options={chartOptions(formatter)}
            series={chartSeries}
            type="area"
          />
          {loading && (
            <div className={styles.loading}>
              <Icon
                icon="spinner-third"
                className="fa-spin"
                style={{
                  animation: "fa-spin 0.5s infinite linear",
                }}
              />
            </div>
          )}
        </div>
        {/* <div
          className={classNames(
            "col-span-1 lg:col-span-1 text-gray-600 flex flex-col space-y-6"
          )}
        >
          <h3 className={classNames("font-bold text-lg leading-none")}>
            Setup guide - Increase revenue
          </h3>
          <iframe
            style={{ width: "100%", height: "250px" }}
            frameBorder="0"
            allowFullScreen="1"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="Setup guide - Increase revenue"
            src="https://www.youtube.com/embed/22qamcR2eOc?autoplay=0&amp;cc_load_policy=1&amp;controls=2&amp;hl=en&amp;rel=0&amp;enablejsapi=1&amp"
          ></iframe>
        </div> */}
      </div>
    </Page>
  );
};

DashboardScreen.propTypes = {};

const Step = ({ index, title, completed = false, children }) => (
  <div
    className={classNames("flex items-start p-3 rounded bg-white shadow")}
    data-completed={completed}
  >
    <span
      className={classNames(
        "bg-orange-400 text-white w-8 h-8 rounded-full inline-flex items-center justify-center mr-3",
        {
          "bg-green-500": completed,
        }
      )}
    >
      {completed ? (
        <Icon icon="check" size="xs" />
      ) : (
        <span className={classNames("font-bold")}>{index}</span>
      )}
    </span>
    <div className={classNames("flex-1")}>
      <h4
        className={classNames("font-bold mb-2 leading-none text-orange-400", {
          "line-through text-green-500": completed,
        })}
      >
        {title}
      </h4>
      <div className={classNames("text-xs text-opacity-50")}>{children}</div>
    </div>
  </div>
);

const StateCard = (props) => {
  const {
    title,
    label,
    value,
    suf,
    pre,
    onClick,
    active,
    currency,
    clickable,
  } = props;
  const {
    state: { money_format },
  } = useContext(ShopContext);
  return (
    // <div className={styles.stateCard} {...{ position, onClick, active }}>
    <div
      className={classNames(
        "shadow flex-1 bg-white rounded p-5 text-gray-600",
        {
          "text-blue-500 ring": active,
        },
        {
          "cursor-pointer": clickable !== false,
        }
      )}
      {...{ onClick, active }}
    >
      {value === null && (
        <ContentLoader>
          <rect x="0" y="0" rx="4" ry="4" width="60%" height="30" />
          <rect x="0" y="60" rx="4" ry="4" width="30%" height="30" />
        </ContentLoader>
      )}
      {typeof value === "number" && (
        <>
          <h3 className={classNames("font-bold mb-3")}>
            {label || _.startCase(title)}
          </h3>
          <h1 className={classNames("font-bold text-4xl")}>
            {pre && <i className={styles.pre}>{pre}</i>}{" "}
            {currency ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMoney(value * 100, money_format),
                }}
              ></span>
            ) : (
              value
            )}{" "}
            {suf && <i className={styles.suf}>{suf}</i>}
          </h1>
        </>
      )}
    </div>
  );
};

const SetupAlert = (props) => {
  const { title, content, value } = props;
  return (
    <div className={styles.SetupAlert}>
      <div className={styles.content}>
        <b>{title}</b>
        <p>{content}</p>
      </div>
      <div className={styles.progress}>
        <CircularProgressbarWithChildren
          value={value}
          // text=
          strokeWidth={10}
          styles={buildStyles({
            // Rotation of path and trail, in number of turns (0-1)
            strokeLinecap: "round",
            textSize: "17px",
            pathTransitionDuration: 0.5,
            pathColor: "#fff",
            textColor: "#fff",
            trailColor: "#286CC8",
            backgroundColor: "#3e98c7",
          })}
        >
          <b>{`${value}%`}</b>
        </CircularProgressbarWithChildren>
      </div>
    </div>
  );
};

export default DashboardScreen;
